import { DeleteOutlined, ExclamationCircleOutlined, LinkOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import type { ActionType, Settings as LayoutSettings, ProColumns } from '@ant-design/pro-components';
import { PageLoading, ProTable, SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import {
  AvatarDropdown,
  AvatarName,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/filmpartner/user';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { Button, Drawer, FloatButton, Form, GetProp, Input, message, Modal, Popconfirm, Select, Tooltip, Upload, UploadFile, UploadProps } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadFile as customUploadFile, deleteUploadItems, uploadItemList } from './services/filmpartner/upload'
import { Typography } from 'antd';
import { getAgentNotice } from './services/filmpartner/agent';
import parse from 'html-react-parser';

const { Text } = Typography;
type FT = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';
const { Search } = Input;

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  showUplodCenter?: boolean,
  showNotice?: boolean,
  commonNotice?: any,
  fetchUserInfo?: () => Promise<User.LoginResult | undefined>;
  uploadFileProcessCallBack?: () => void;
}> {
  const fetchUserInfo = async () => {
    try {
      Input
      const result = await queryCurrentUser({
        skipErrorHandler: true,
      });
      if (result.msg == "UNAUTHORIZED") {
        return undefined;
      }
      return result.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}


/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 拦截器在 requestErrorConfig文 件中已定义，此处定义不会起作用，但是不在app.ts注册会不能初始化
  // baseURL: '/api/Agent',
  ...errorConfig,
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const columns: ProColumns<Common.UploadItem>[] = [
    {
      title: '文件名称',
      dataIndex: 'fileName'
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      renderText: (val: string) => {
        if (val == "AgentSample") {
          return "小样";
        }
        if (val == "AgentAttachment") {
          return "附件";
        }
        return val;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      hideInSearch: true
    },
    {
      title: '状态',
      dataIndex: 'fileStatus',
      valueEnum: {
        '0': {
          text: '上传中',
          status: 'Processing',
        },
        '1': {
          text: '待解析',
          status: 'default',
        },
        '2': {
          text: '解析中',
          status: 'warning',
        },
        '3': {
          text: '已完成',
          status: 'success',
        },
        '-1': {
          text: '上传失败',
          status: 'error',
        },
        '-2': {
          text: '解析失败',
          status: 'error',
        },

      },
      render: (dom, entity) => {
        let doms = [];
        doms.push(dom);
        if (entity.fileStatus == '-1' || entity.fileStatus == '-2') {
          doms.push(
            <Tooltip title={entity.failReason}>
              <Button style={{ marginLeft: 5 }} size="small"
                icon={<ExclamationCircleOutlined />} variant="link" color="red" />
            </Tooltip>);
        }
        return doms;
      }
    },
    {
      title: '上传进度',
      dataIndex: 'progress',
      valueType: 'progress',
      hideInSearch: true
    },
    {
      title: '操作',
      dataIndex: 'option',
      valueType: 'option',
      render: (_, record) => {
        let doms = [];
        doms.push(<Popconfirm
          title="确认删除？"
          onConfirm={() => {
            deleteUploadItems(record).then(x => {
              message.success('删除成功');
              actionRef.current?.reload();
            })
          }}
          okText="是"
          cancelText="否" >
          <Button icon={<DeleteOutlined />} color="danger" variant="filled" size="small">
            删除
          </Button>
        </Popconfirm>)
        return doms;
      }
    }
  ];
  const [form] = Form.useForm();
  const actionRef = useRef<ActionType>(null);
  const [upLoadVisible, setUpLoadVisible] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedFileName, setSelectedFileName] = useState<string>();
  const [fileType, setFileType] = useState<string>("DCP");
  const [upLoadingState, setUpLoadingState] = useState<any>();

  setInitialState({
    ...initialState, uploadFileProcessCallBack: useCallback(() => {
      actionRef.current?.reload();
    }, [])
  })

  useEffect(() => {
    if (initialState?.commonNotice === undefined && initialState?.currentUser?.userType === "agent") {
      getAgentNotice().then(v => {
        console.log(v.data);
        if (v.data.indexOf("系统公告") < 0) {
          setInitialState((s) => ({
            ...s,
            showNotice: true,
            commonNotice: parse(v.data)
          }));
        }
      });
    }
  }, []);

  const getUploadList = async (body: {
    current?: number;
    pageSize?: number;
    fileName?: string;
    fileStatus?: string
  }) => {
    const pageResult = await uploadItemList(body);
    const notFinish = pageResult.data.filter(x => x.progress !== undefined
      && x.progress < 100 && x.fileStatus == "0").length
    notFinish > 0 ? setUpLoadingState({ count: notFinish, overflowCount: 9 })
      : setUpLoadingState(undefined);
    return pageResult;
  }

  const props: UploadProps = {
    itemRender: (() => { }),
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setSelectedFileName(file.name);
      var newFileList = [];
      newFileList.push(file);
      setFileList(newFileList);
      return false;
    },
    fileList,
  };

  const uploadFile = async () => {
    if (await form.validateFields()) {
      var tempFile = fileList[0];
      const filesize = tempFile.size ?? 0;
      const fileID = new Date().getTime() + "-" + tempFile.name;

      const formData = new FormData();
      formData.append("fileID", fileID);
      formData.append("file", fileList[0] as FT);
      customUploadFile(formData, {
        fileID: fileID,
        fileName: tempFile.name,
        fileSize: filesize,
        fileType: fileType
      },
        () => {
          actionRef.current?.reload();
        }).then(() => actionRef.current?.reload());
      form.resetFields();
      setSelectedFileName(undefined);
      setUpLoadVisible(false);
    }
  }

  return {
    avatarProps: {
      src: <UserOutlined style={{ color: '#0ea4ea' }} />, //initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      //content: initialState?.currentUser?.nickName,
    },
    //footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    menu: {
      params: {
        userType: initialState?.currentUser?.userType,
      },
      request: async (params, defaultMenuData) => {
        if (initialState?.currentUser?.userType !== undefined && !isDev) {
          const authMenu = defaultMenuData.filter(x => x.path !== undefined && (x.path?.indexOf("agent") > 0 || x.path?.indexOf("acount") > 0));
          return authMenu;
        }
        return defaultMenuData;
      },
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [

        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>OpenAPI 文档</span>
        </Link>,
      ]
      : [],
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
          {initialState?.currentUser?.userType === "agent" && (<FloatButton
            description='上传'
            style={{ marginBottom: 40, marginRight: -10 }}
            shape='square'
            type='primary'
            tooltip='上传中心'
            icon={<UploadOutlined />}
            badge={upLoadingState}
            onClick={() => setInitialState((preInitialState) => ({
              ...preInitialState,
              showUplodCenter: true,
            }))} />)
          }
          {initialState?.currentUser?.userType === "agent" && (<FloatButton
            description='公告'
            style={{ marginBottom: 100, marginRight: -10 }}
            shape='square'
            type='primary'
            tooltip='系统公告'
            icon={<ExclamationCircleOutlined />}
            onClick={() => setInitialState((preInitialState) => ({
              ...preInitialState,
              showNotice: true,
            }))} />)
          }
          <Modal title="系统公告"
            open={initialState?.showNotice}
            width={800}
            footer={false}
            onCancel={() => setInitialState((preInitialState) => ({
              ...preInitialState,
              showNotice: false,
            }))} >
            {initialState?.commonNotice}
          </Modal>
          <Drawer
            title='上传中心'
            width={900}
            open={initialState?.showUplodCenter}
            onClose={() => {
              setInitialState((preInitialState) => ({
                ...preInitialState,
                showUplodCenter: false,
              }))
            }}
          >
            <Text style={{ marginLeft: 25 }} type="danger">
              注意：未上传完成时请勿 刷新/关闭 网站，否则会导致上传失败。
              上传记录最多保存7天
            </Text>
            <ProTable<Common.UploadItem, Common.PageSearch>
              rowKey="fileID"
              actionRef={actionRef}
              search={{
                labelWidth: 'auto'
              }}
              // debounceTime={1000}
              defaultSize="small"
              rowSelection={false}
              tableAlertRender={false}
              pagination={{ defaultPageSize: 15 }}
              loading={false}
              toolBarRender={() => [
                <Button
                  type="primary"
                  key="uploadFile"
                  onClick={() => {
                    setUpLoadVisible(true);
                  }}
                >
                  <UploadOutlined /> 上传
                </Button>,
              ]}
              request={getUploadList}
              columns={columns}

            />
            <Modal
              width={450}
              title="上传文件"
              open={upLoadVisible}
              onOk={uploadFile}
              onCancel={() => {
                setUpLoadVisible(false);
                form.resetFields();
                setSelectedFileName(undefined);
              }
              }>
              <Form
                form={form}
                initialValues={{ fileType }}>
                <Form.Item
                  label="文件类型"
                  name="fileType"
                  rules={[{ required: true, message: '请选择文件类型' }]}
                >
                  <Select
                    style={{ width: 275 }}
                    onChange={(val) => setFileType(val)}
                    options={[
                      { value: 'DCP', label: 'DCP' },
                      { value: 'others', label: '其他', disabled: true },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label="文件名称"
                  name="fileName"
                  rules={[{
                    required: true,
                    validator: (_, value) => {
                      if (value === undefined || value == "") {
                        return Promise.reject(new Error('请选择要上传的文件'));
                      }
                      else {
                        if (fileType == "DCP" && !value.file.name.toLocaleLowerCase().endsWith(".rar")) {
                          return Promise.reject(new Error('必须上传rar压缩包'));
                        }
                      }
                      return Promise.resolve();
                    }
                  }]}
                >
                  <Upload {...props}>
                    <Search
                      placeholder="请选择文件..."
                      allowClear
                      enterButton="选择"
                      size="middle"
                      onSearch={() => { }}
                      onClear={() => {
                        setSelectedFileName(undefined);
                      }}
                      value={selectedFileName}
                    />
                  </Upload>
                </Form.Item>
              </Form>
            </Modal>
          </Drawer >
        </>
      );
    },
    ...initialState?.settings,
  };
};


